'use client';

import { Minus, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Popover, PopoverContent, PopoverTrigger } from '@/src/shared/ui/popover';
import { cn } from '@/src/shared/lib/shadcn';
import { useHoverablePopover } from '@/src/shared/lib/useHoverablePopover';
import useLocaleCurrency from '@/src/shared/lib/useLocaleCurrency';
import {
  CHANGE_CAP,
  CHANGE_DECIMALS,
  type ChangeUnit,
  changeAmount,
  isCappedChange,
  roundChange,
} from '@/src/shared/ui/kpi-change.utils';

const ICONS = { up: TrendingUp, down: TrendingDown, flat: Minus, none: null } as const;

type Direction = keyof typeof ICONS;

const TONE_INK = {
  positive: 'text-(--sc-pos-ink)',
  negative: 'text-(--sc-neg-ink)',
  warning: 'text-(--sc-warn-ink)',
  neutral: 'text-muted-foreground',
} as const;

const directionOf = (shown: number | null): Direction => {
  if (shown === null) return 'none';
  if (shown > 0) return 'up';
  if (shown < 0) return 'down';
  return 'flat';
};

export interface KpiChangeProps {
  /** null when there is no earlier period to compare against. */
  change: number | null;
  /** Sentence saying what the figure measures and against what, shown on hover, tap or focus. */
  hint: string;
  /** Replaces the printed change with a ready-made line, such as an amount; `change` still sets the arrow. */
  figure?: string;
  /** `points` for the difference between two rates, which is not a percentage of anything. */
  unit?: ChangeUnit;
  /** The direction that is good news; spending is better when it goes down. */
  goodWhen?: 'up' | 'down';
  /** Replaces the arrow when the line states a condition rather than a movement; `change` still sets the colour. */
  icon?: LucideIcon;
  /** Sets the colour when the line judges a level against a goal, so it matches its card rather than the change's sign. */
  tone?: keyof typeof TONE_INK;
  className?: string;
}

export const KpiChange = ({
  change,
  hint,
  figure: ownFigure,
  unit = 'percent',
  goodWhen = 'up',
  icon,
  tone,
  className,
}: KpiChangeProps) => {
  const t = useTranslations('common.kpiChange');
  const { formatPercent, locale } = useLocaleCurrency();
  const { open, onOpenChange, hoverProps, contentProps } = useHoverablePopover();

  // Direction comes from the value as displayed, not the raw one: a change of
  // 0.04 would otherwise pair a green arrow with "0,0%". A value that is not a
  // number has nothing to show, like a missing one.
  const shown = change === null || Number.isNaN(change) ? null : roundChange(change);
  // An unknown change and a measured zero share the grey, never the text:
  // "0,0%" claims the value held steady, which nothing here can back up.
  const direction = directionOf(shown);
  // No icon without a figure: a dash glyph beside the long dash reads as two separate strokes.
  const Icon = direction === 'none' ? null : (icon ?? ICONS[direction]);
  const moved = direction === 'up' || direction === 'down';
  const colour =
    tone && shown !== null
      ? TONE_INK[tone]
      : !moved
        ? 'text-muted-foreground'
        : direction === goodWhen
          ? 'text-(--sc-pos-ink)'
          : 'text-(--sc-neg-ink)';
  const capped = !ownFigure && shown !== null && isCappedChange(shown);

  const figure = () => {
    // The plain dash a financial table uses for a value that does not exist.
    if (shown === null) return '—';
    if (ownFigure) return ownFigure;
    const sign = shown > 0 ? '+' : '';
    if (unit === 'points') {
      const points = changeAmount(shown, unit, { locale, formatPercent });
      if (capped) return `${shown > 0 ? points : points.replace('>', '<-')} ${t('points')}`;
      return `${shown < 0 ? '-' : sign}${points} ${t('points')}`;
    }
    if (capped) {
      return shown > 0 ? `>${formatPercent(CHANGE_CAP, 0)}` : `<${formatPercent(-CHANGE_CAP, 0)}`;
    }
    return `${sign}${formatPercent(shown, CHANGE_DECIMALS)}`;
  };

  return (
    <div className={cn('flex items-center', className)}>
      {/* Popover rather than Tooltip because Radix tooltips never open on tap. */}
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger
          type="button"
          data-testid="kpi-change"
          data-tone={direction}
          // The line is shorter, and a lone dash narrower, than a comfortable touch
          // target, so a transparent pseudo-element extends the hit area both ways
          // without moving anything.
          className={cn(
            "relative flex items-center gap-1 rounded-sm text-left text-xs font-semibold outline-none before:absolute before:-inset-y-3.5 before:left-1/2 before:w-[max(calc(100%+1rem),2.75rem)] before:-translate-x-1/2 before:content-[''] focus-visible:ring-[3px] focus-visible:ring-ring/50",
            colour,
          )}
          {...hoverProps}
        >
          {Icon && <Icon className="h-3 w-3" aria-hidden />}
          <span className="tabular-nums">{figure()}</span>
          <span className="sr-only">{t('label')}</span>
        </PopoverTrigger>
        <PopoverContent
          data-testid="kpi-change-hint"
          className="w-72 p-3 text-center text-xs"
          {...contentProps}
          {...hoverProps}
        >
          {shown === null ? t('noData') : hint}
          {capped && <p className="mt-1">{t('capped')}</p>}
        </PopoverContent>
      </Popover>
    </div>
  );
};
